import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import Intercom, { boot, show, shutdown } from '@intercom/messenger-js-sdk'

import { deleteCookiesByPrefix } from '@/lib/consent'
import { useStores } from '@/components/StoreProvider'
import { ChatConsentLauncher } from '@/components/Consent/ChatConsentLauncher'

const APP_ID = 'z4eeplvl'

/**
 * Intercom sets its own cookies, so it loads only once the visitor allows functional cookies
 * and is shut down again if they withdraw.
 */
export const IntercomProvider = observer(() => {
  const { userStore, consentStore } = useStores()
  const user = userStore.user
  const allowed = consentStore.functional

  // The SDK's Intercom() only works once per page; later changes go through shutdown/boot
  const loaded = useRef(false)
  const openAfterBoot = useRef(false)

  useEffect(() => {
    if (!allowed) {
      if (loaded.current) shutdown()
      deleteCookiesByPrefix('intercom-')
      Object.keys(localStorage)
        .filter((key) => key.startsWith('intercom.'))
        .forEach((key) => localStorage.removeItem(key))
      return
    }

    const settings = user
      ? {
          app_id: APP_ID,
          intercom_user_jwt: user.intercom_jwt,
          user_id: String(user.id),
          name: user.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : undefined,
          email: user.email ?? undefined,
        }
      : { app_id: APP_ID }

    if (loaded.current) {
      shutdown()
      boot(settings)
    } else {
      Intercom(settings)
      loaded.current = true
    }

    if (openAfterBoot.current) {
      openAfterBoot.current = false
      show()
    }
  }, [user, allowed])

  return <ChatConsentLauncher onEnable={() => (openAfterBoot.current = true)} />
})
IntercomProvider.displayName = 'IntercomProvider'
